import React, { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, LayoutTemplate, X, Upload } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Shell, Empty } from "@/components/Shell";
import { PlanImport } from "@/components/PlanImport";
import { Select } from "@/pages/Projects";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const BLANK = { name: "", framework: "", description: "", tasks: [] };
const BLANK_TASK = { name: "", stage: "", offset_days: 0, duration_days: 5, is_milestone: false, outline_level: 1, resources: "", predecessors: [] };
const LINK_TYPES = ["FS", "SS", "FF", "SF"];

export default function AdminScheduleTemplates() {
  const [templates, setTemplates] = useState([]);
  const [frameworks, setFrameworks] = useState([]);
  const [stages, setStages] = useState([]);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);

  const load = () =>
    api
      .get("/schedule-templates")
      .then(({ data }) => setTemplates(data))
      .catch((e) => toast.error(apiError(e.response?.data?.detail) || "Could not load templates"));

  useEffect(() => {
    load();
    api.get("/config/lists").then(({ data }) => {
      const byKey = Object.fromEntries(data.map((l) => [l.key, l.values]));
      setFrameworks(byKey.governance_framework || []);
      setStages(byKey.stage || []);
    }).catch(() => toast.error("Could not load framework and stage options"));
  }, []);

  const openTemplate = (t) => {
    setEditing(t || null);
    setForm(t ? { ...BLANK, ...t, tasks: t.tasks || [] } : BLANK);
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Template name is required");
    if (form.tasks.length === 0) return toast.error("Add at least one task");
    const payload = {
      name: form.name.trim(),
      framework: form.framework || null,
      description: form.description || null,
      tasks: form.tasks.map((t) => ({
        name: t.name,
        stage: t.stage || null,
        offset_days: Number(t.offset_days) || 0,
        duration_days: Number(t.duration_days) || 0,
        is_milestone: !!t.is_milestone,
        outline_level: Number(t.outline_level) || 1,
        parent_index: t.parent_index ?? null,
        resources: t.resources || null,
        work_hours: t.work_hours ? Number(t.work_hours) : null,
        notes: t.notes || null,
        percent_complete: 0,
        predecessors: (t.predecessors || [])
          .filter((p) => p.index !== "" && p.index !== null && p.index !== undefined)
          .map((p) => ({ index: Number(p.index), type: p.type || "FS", lag_days: Number(p.lag_days) || 0 })),
      })),
    };
    try {
      if (editing) await api.put(`/schedule-templates/${editing.id}`, payload);
      else await api.post("/schedule-templates", payload);
      setOpen(false);
      await load();
      toast.success(editing ? "Template updated" : "Template created");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete the template "${t.name}"?`)) return;
    try {
      await api.delete(`/schedule-templates/${t.id}`);
      await load();
      toast.success("Template deleted");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const setTask = (i, key, value) =>
    setForm((f) => ({ ...f, tasks: f.tasks.map((t, idx) => (idx === i ? { ...t, [key]: value } : t)) }));

  return (
    <Shell
      testId="admin-templates-page"
      title="Schedule Templates"
      subtitle="Standard plans per governance framework. PMs apply these on a project's Plan tab."
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" data-testid="import-template-btn" onClick={() => setImportOpen(true)} className="gap-2">
            <Upload size={15} /> Import Plan File
          </Button>
          <Button data-testid="new-template-btn" onClick={() => openTemplate(null)} className="gap-2" style={{ backgroundColor: "var(--brand-primary)" }}>
            <Plus size={15} /> New Template
          </Button>
        </div>
      }
    >
      {templates.length === 0 ? (
        <Empty testId="templates-empty" text="No schedule templates yet." />
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {templates.map((t) => (
            <div key={t.id} data-testid={`template-card-${t.id}`} className="panel p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-display text-base font-semibold text-slate-900">{t.name}</h3>
                  <div className="mt-0.5 text-xs text-slate-400">
                    {t.framework || "No framework"} · {t.tasks?.length || 0} tasks ·{" "}
                    {t.tasks?.filter((x) => x.is_milestone).length || 0} milestones
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button data-testid={`edit-template-${t.id}`} onClick={() => openTemplate(t)} aria-label="Edit template" title="Edit template" className="text-slate-400 hover:text-slate-800">
                    <Pencil size={15} />
                  </button>
                  <button data-testid={`delete-template-${t.id}`} onClick={() => remove(t)} aria-label="Delete template" title="Delete template" className="text-slate-300 hover:text-red-600">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
              {t.description && <p className="mt-2 text-sm text-slate-600">{t.description}</p>}
              <ol className="mt-3 space-y-1 border-t border-slate-100 pt-3 text-xs text-slate-600">
                {(t.tasks || []).slice(0, 6).map((x, i) => (
                  <li key={`${t.id}-${i}`} className="flex items-center justify-between gap-3">
                    <span className="truncate">{x.is_milestone ? "◆ " : ""}{x.name}</span>
                    <span className="shrink-0 text-slate-400">day {x.offset_days}{x.duration_days ? ` +${x.duration_days}d` : ""}</span>
                  </li>
                ))}
                {(t.tasks?.length || 0) > 6 && <li className="text-slate-400">+{t.tasks.length - 6} more…</li>}
              </ol>
            </div>
          ))}
        </div>
      )}

      <PlanImport open={importOpen} onOpenChange={setImportOpen} onImported={load} />


      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="template-dialog" className="max-h-[85vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Template" : "New Template"}</DialogTitle>
            <DialogDescription>Offset is days from the plan start date. Duration 0 or a milestone tick creates a point-in-time milestone.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div><label className="field-label">Template Name *</label><Input data-testid="template-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div>
              <label className="field-label">Governance Framework</label>
              <Select testId="template-framework-select" value={form.framework} onChange={(v) => setForm({ ...form, framework: v })} options={frameworks} placeholder="Select framework" />
            </div>
            <div className="sm:col-span-2"><label className="field-label">Description</label><Textarea data-testid="template-description-input" rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          </div>

          <div className="mt-2">
            <div className="mb-2 flex items-center justify-between">
              <h4 className="font-display text-sm font-semibold text-slate-800">Template Tasks</h4>
              <Button size="sm" variant="outline" data-testid="add-template-task-btn" onClick={() => setForm((f) => ({ ...f, tasks: [...f.tasks, { ...BLANK_TASK }] }))} className="gap-1.5">
                <Plus size={13} /> Add Task
              </Button>
            </div>
            {form.tasks.length === 0 ? (
              <p className="rounded border border-dashed border-slate-200 py-6 text-center text-sm text-slate-400">No tasks yet.</p>
            ) : (
              <div className="space-y-2">
                {form.tasks.map((t, i) => (
                  <div key={i} data-testid={`template-task-row-${i}`} className="rounded border border-slate-200 p-2">
                    <div className="grid grid-cols-12 items-end gap-2">
                      <div className="col-span-4">
                        <label className="field-label">Task {i + 1}</label>
                        <Input data-testid={`template-task-name-${i}`} value={t.name} onChange={(e) => setTask(i, "name", e.target.value)} />
                      </div>
                      <div className="col-span-2"><label className="field-label">Stage</label><Select testId={`template-task-stage-${i}`} value={t.stage} onChange={(v) => setTask(i, "stage", v)} options={stages} placeholder="Stage" /></div>
                      <div className="col-span-1"><label className="field-label">Level</label><Input data-testid={`template-task-level-${i}`} type="number" min="1" max="12" value={t.outline_level ?? 1} onChange={(e) => setTask(i, "outline_level", Number(e.target.value) || 1)} /></div>
                      <div className="col-span-1"><label className="field-label">Offset</label><Input data-testid={`template-task-offset-${i}`} type="number" min="0" value={t.offset_days} onChange={(e) => setTask(i, "offset_days", e.target.value)} /></div>
                      <div className="col-span-1"><label className="field-label">Days</label><Input data-testid={`template-task-duration-${i}`} type="number" min="0" value={t.duration_days} onChange={(e) => setTask(i, "duration_days", e.target.value)} /></div>
                      <div className="col-span-2"><label className="field-label">Resource</label><Input data-testid={`template-task-resources-${i}`} value={t.resources || ""} onChange={(e) => setTask(i, "resources", e.target.value)} /></div>
                      <div className="col-span-1 flex items-center justify-between gap-1 pb-2">
                        <input data-testid={`template-task-milestone-${i}`} type="checkbox" title="Milestone" aria-label="Milestone" className="h-4 w-4" checked={!!t.is_milestone} onChange={(e) => setTask(i, "is_milestone", e.target.checked)} />
                        <button data-testid={`remove-template-task-${i}`} onClick={() => setForm((f) => ({ ...f, tasks: f.tasks.filter((_, idx) => idx !== i) }))} aria-label="Remove task" title="Remove task" className="text-slate-300 hover:text-red-600">
                          <X size={14} />
                        </button>
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-2">
                      <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Depends on</span>
                      {(t.predecessors || []).map((p, pi) => (
                        <span key={pi} className="inline-flex items-center gap-1 rounded bg-slate-50 px-1.5 py-1">
                          <select data-testid={`template-pred-task-${i}-${pi}`} value={p.index ?? ""} className="h-7 rounded border border-slate-300 text-xs"
                            onChange={(e) => setTask(i, "predecessors", t.predecessors.map((x, xi) => (xi === pi ? { ...x, index: e.target.value } : x)))}>
                            <option value="">task…</option>
                            {form.tasks.map((o, oi) => oi !== i && <option key={oi} value={oi}>{oi + 1}. {o.name || "untitled"}</option>)}
                          </select>
                          <select data-testid={`template-pred-type-${i}-${pi}`} value={p.type || "FS"} className="h-7 rounded border border-slate-300 text-xs"
                            onChange={(e) => setTask(i, "predecessors", t.predecessors.map((x, xi) => (xi === pi ? { ...x, type: e.target.value } : x)))}>
                            {LINK_TYPES.map((x) => <option key={x} value={x}>{x}</option>)}
                          </select>
                          <Input data-testid={`template-pred-lag-${i}-${pi}`} type="number" value={p.lag_days ?? 0} className="h-7 w-16 text-xs"
                            onChange={(e) => setTask(i, "predecessors", t.predecessors.map((x, xi) => (xi === pi ? { ...x, lag_days: e.target.value } : x)))} />
                          <button data-testid={`remove-template-pred-${i}-${pi}`} aria-label="Remove dependency" className="text-slate-300 hover:text-red-600"
                            onClick={() => setTask(i, "predecessors", t.predecessors.filter((_, xi) => xi !== pi))}><X size={12} /></button>
                        </span>
                      ))}
                      <button data-testid={`add-template-pred-${i}`} onClick={() => setTask(i, "predecessors", [...(t.predecessors || []), { index: "", type: "FS", lag_days: 0 }])}
                        className="text-[11px] font-semibold text-slate-500 hover:text-slate-900">+ dependency</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" data-testid="template-cancel-btn" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="template-save-btn" onClick={save} className="gap-1.5" style={{ backgroundColor: "var(--brand-primary)" }}>
              <LayoutTemplate size={14} /> {editing ? "Save Template" : "Create Template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
